import type { Resource } from "./schemas";

/**
 * Who among the attendees may see a resource.
 *
 * The document carries `audience` ("everyone" or "tracks") and `trackIds`;
 * firestore.rules validates and enforces the pair. The Storage object carries
 * a copy in its custom metadata, because storage.rules can afford to read only
 * the caller's membership, not the resource document. Both are written from
 * here so they cannot be spelled differently.
 */
export type ResourceAudience = {
  audience: "everyone" | "tracks";
  trackIds: string[];
};

/** Matches the list bound in firestore.rules (isValidResourceAudience). */
export const MAX_RESOURCE_TRACKS = 20;

/** No tracks chosen means everyone. */
export function audienceFor(trackIds: string[]): ResourceAudience {
  const unique = [...new Set(trackIds)];
  return unique.length === 0
    ? { audience: "everyone", trackIds: [] }
    : { audience: "tracks", trackIds: unique };
}

/**
 * The Storage object's copy. Commas cannot occur in a track id: the rules
 * refuse them in any document id list.
 */
export function audienceMetadata(
  audience: ResourceAudience,
): Record<string, string> {
  return {
    audience: audience.audience,
    trackIds: audience.trackIds.join(","),
  };
}

/**
 * How the admin list describes a resource's audience.
 *
 * "admins" is a resource limited to tracks that no longer has any, which is
 * what deleting its last track leaves: attendees cannot see it, and widening
 * it to everyone is left to the admin. A resource from before the field
 * existed reads as everyone, which the backfill makes true.
 */
export function describeAudience(
  resource: Pick<Resource, "audience" | "trackIds">,
  trackNames: ReadonlyMap<string, string>,
):
  | { kind: "everyone" }
  | { kind: "admins" }
  | { kind: "tracks"; names: string[] } {
  if (resource.audience !== "tracks") return { kind: "everyone" };
  const names = (resource.trackIds ?? [])
    .map((id) => trackNames.get(id))
    .filter((name): name is string => name !== undefined);
  return names.length === 0 ? { kind: "admins" } : { kind: "tracks", names };
}

/** Track ids the resource still names that exist, for pre-filling a form. */
export function liveTrackIds(
  resource: Pick<Resource, "trackIds">,
  trackNames: ReadonlyMap<string, string>,
): string[] {
  return (resource.trackIds ?? []).filter((id) => trackNames.has(id));
}

/**
 * The attendee's listing, merged from its two queries and ordered the way the
 * single query used to order it. A resource can come back from both only if
 * its data is inconsistent, so it is de-duplicated by id regardless.
 */
export function mergeResources(...lists: Resource[][]): Resource[] {
  const byId = new Map<string, Resource>();
  for (const list of lists) {
    for (const resource of list) byId.set(resource.id, resource);
  }
  return [...byId.values()].sort(
    (a, b) => uploadedMillis(a) - uploadedMillis(b),
  );
}

function uploadedMillis(resource: Resource): number {
  const value: unknown = resource.uploadedAt;
  if (value && typeof value === "object" && "toMillis" in value) {
    const toMillis = (value as { toMillis: unknown }).toMillis;
    if (typeof toMillis === "function") return toMillis.call(value) as number;
  }
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return Number.MAX_SAFE_INTEGER;
}
